import type { Department } from '@/lib/departments'

export type RuleAction =
  | 'create_inquiry'
  | 'forward_to_role'
  | 'forward_to_email'
  | 'auto_reply'
  | 'archive'
  | 'mark_spam'
  | 'automated_transactional'

export type TeamRole =
  | 'manager'
  | 'tour_manager'
  | 'production_manager'
  | 'marketing'
  | 'press'
  | 'legal'
  | 'accounting'
  | 'booking_agent'
  | 'personal_assistant'
  | 'security'
  | 'wardrobe'
  | 'medic'
  | 'social_media'
  | 'other'

export interface RuleConditions {
  classificationType?: string
  fromDomain?: string
  subjectContains?: string
  bodyContains?: string
  dueDiligenceScore?: string
}

export interface Mailbox {
  id: string
  address: string
  label: string
  description: string | null
  isDefault: boolean
  isActive: boolean
  _count?: { rules: number }
}

export interface RoutingRule {
  id: string
  name: string
  description: string | null
  conditions: RuleConditions
  action: RuleAction
  targetRole: TeamRole | null
  targetDepartment: Department | null
  targetPersonId: string | null
  targetEmail: string | null
  priority: number
  enabled: boolean
  parentRuleId: string | null
  mailboxId: string | null
  mailbox?: Mailbox | null
  children?: RoutingRule[]
}

export const RULE_ACTIONS: RuleAction[] = [
  'forward_to_role',
  'forward_to_email',
  'create_inquiry',
  'auto_reply',
  'archive',
  'mark_spam',
  'automated_transactional',
]

export const TEAM_ROLES: TeamRole[] = [
  'manager',
  'tour_manager',
  'production_manager',
  'marketing',
  'press',
  'legal',
  'accounting',
  'booking_agent',
  'personal_assistant',
  'security',
  'wardrobe',
  'medic',
  'social_media',
  'other',
]

/** AI classification types the ingestor can emit — used as branch presets. */
export const CLASSIFICATION_PRESETS: Array<{ value: string; label: string; group: string }> = [
  { value: 'legal', label: 'Legal', group: 'Business' },
  { value: 'accounts', label: 'Accounts / Finance', group: 'Business' },
  { value: 'licensing', label: 'Licensing', group: 'Business' },
  { value: 'press', label: 'Press / Media', group: 'Business' },
  { value: 'broker_application', label: 'Broker application', group: 'Business' },
  { value: 'public_concert', label: 'Public concert', group: 'Bookings' },
  { value: 'festival', label: 'Festival', group: 'Bookings' },
  { value: 'corporate_gala', label: 'Corporate / Gala', group: 'Bookings' },
  { value: 'private_event', label: 'Private event', group: 'Bookings' },
  { value: 'casino_pac_theatre', label: 'Casino / PAC / Theatre', group: 'Bookings' },
  { value: 'international', label: 'International', group: 'Bookings' },
  { value: 'residency', label: 'Residency', group: 'Bookings' },
  { value: 'destination', label: 'Destination', group: 'Bookings' },
]

/** Suggested primary branches when clicking "+ Add branch". */
export const BRANCH_PRESETS: Array<{
  name: string
  conditions: RuleConditions
  department?: Department
  role?: TeamRole
}> = [
  {
    name: 'Legal',
    conditions: { classificationType: 'legal' },
    department: 'legal_contracts',
    role: 'legal',
  },
  {
    name: 'Bookings',
    conditions: { classificationType: 'public_concert' },
    department: 'sales_bookings',
    role: 'booking_agent',
  },
  {
    name: 'Accounts',
    conditions: { classificationType: 'accounts' },
    department: 'finance_accounts',
    role: 'accounting',
  },
  {
    name: 'Press & PR',
    conditions: { classificationType: 'press' },
    department: 'marketing_pr',
    role: 'press',
  },
  {
    name: 'Licensing',
    conditions: { classificationType: 'licensing' },
    department: 'legal_contracts',
  },
]

export function conditionsAreEmpty(c: RuleConditions): boolean {
  return (
    !c.classificationType &&
    !c.fromDomain &&
    !c.subjectContains &&
    !c.bodyContains &&
    !c.dueDiligenceScore
  )
}

export function isCatchAllRule(rule: RoutingRule): boolean {
  return conditionsAreEmpty(rule.conditions) && rule.priority >= 500
}

export function isSystemGuardRule(rule: RoutingRule): boolean {
  return (
    !rule.parentRuleId &&
    (rule.action === 'mark_spam' || rule.action === 'automated_transactional')
  )
}

export function summariseConditions(c: RuleConditions): string {
  const parts: string[] = []
  if (c.classificationType) parts.push(`AI category = ${c.classificationType.replace(/_/g, ' ')}`)
  if (c.fromDomain) parts.push(`from @${c.fromDomain}`)
  if (c.subjectContains) parts.push(`subject contains “${c.subjectContains}”`)
  if (c.bodyContains) parts.push(`body contains “${c.bodyContains}”`)
  return parts.length > 0 ? parts.join(' · ') : 'Matches everything (catch-all)'
}

export function formatDestination(rule: RoutingRule): string {
  if (rule.targetEmail) return rule.targetEmail
  if (rule.targetDepartment) return rule.targetDepartment.replace(/_/g, ' ')
  if (rule.targetRole) return rule.targetRole.replace(/_/g, ' ')
  if (rule.action === 'create_inquiry') return 'Create inquiry'
  if (rule.action === 'mark_spam') return 'Spam folder'
  if (rule.action === 'archive') return 'Archive'
  return rule.action.replace(/_/g, ' ')
}

export function flattenRules(rules: RoutingRule[]): RoutingRule[] {
  const out: RoutingRule[] = []
  const seen = new Set<string>()
  function walk(r: RoutingRule) {
    if (seen.has(r.id)) return
    seen.add(r.id)
    out.push(r)
    for (const child of r.children ?? []) walk(child)
  }
  for (const r of rules) walk(r)
  return out
}

export function findRule(rules: RoutingRule[], id: string): RoutingRule | undefined {
  return flattenRules(rules).find((r) => r.id === id)
}

export function topLevelRules(rules: RoutingRule[]): RoutingRule[] {
  return rules.filter((r) => r.parentRuleId === null)
}

export function branchAccent(name: string, conditions: RuleConditions): string {
  const t = conditions.classificationType ?? name.toLowerCase()
  if (t.includes('legal') || t.includes('licensing') || t.includes('copyright')) {
    return 'border-l-violet-500'
  }
  if (t.includes('account') || t.includes('finance')) return 'border-l-emerald-500'
  if (t.includes('press') || t.includes('market')) return 'border-l-sky-500'
  if (
    t.includes('book') ||
    t.includes('concert') ||
    t.includes('festival') ||
    t.includes('event')
  ) {
    return 'border-l-amber-500'
  }
  return 'border-l-indigo-500'
}
